import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import { useSocket } from "./SocketContext";
import API from "../api";
import { admissionMatchesAnyCachedView, broadcastAdmissionDelete, broadcastAdmissionUpsert, catchUpAllViews, clearAllAdmissionViews, findAdmissionInCachedViews, getOrCreateAdmissionView } from "../db/admissionView";
import { serializeFilterSpec } from "../db/admissionFilter";
import { clearAdmissions, prefetchAdmission } from "../db/admissions";
import { ADMISSION_STATUS } from "../utils";

const DEFAULT_DOCUMENT_TITLE = "Querco Extraction - Admin";
const TO_PROCESS_RECENT_DAYS = 7;
// Every client on the tenant receives the same upsert; the random delay spreads the
// resulting prefetch GETs so they don't hit the API as one synchronized burst.
const EVENT_PREFETCH_JITTER_MS = 3000;

const TO_PROCESS_ATTENTION_SPEC = {
  status: [ADMISSION_STATUS.ERROR, ADMISSION_STATUS.TO_VALIDATE],
  dateWithinDays: TO_PROCESS_RECENT_DAYS,
};

const AdmissionsContext = createContext({ ready: false, sessionEpoch: 0 });

export const useAdmissionView = (spec) => {
  const { ready, sessionEpoch } = useContext(AdmissionsContext);
  const key = serializeFilterSpec(spec);
  return useMemo(() => {
    if (!ready) return null;
    if (!API.getToken()) return null;
    return getOrCreateAdmissionView(spec);
  }, [ready, key, sessionEpoch]);
};

export const useAdmissionPreview = (id) => {
  const { ready, sessionEpoch } = useContext(AdmissionsContext);
  return useMemo(() => (ready ? findAdmissionInCachedViews(id) : null), [ready, id, sessionEpoch]);
};

const AdmissionTabAttention = () => {
  const collection = useAdmissionView(TO_PROCESS_ATTENTION_SPEC);
  const { data: rawAdmissions } = useLiveQuery((q) => (collection ? q.from({ admission: collection }) : null), [collection]);
  const toProcessCount = collection ? rawAdmissions?.length || 0 : 0;

  useEffect(() => {
    document.title = toProcessCount > 0 ? `(${toProcessCount}) ${DEFAULT_DOCUMENT_TITLE}` : DEFAULT_DOCUMENT_TITLE;
    return () => {
      document.title = DEFAULT_DOCUMENT_TITLE;
    };
  }, [toProcessCount]);

  return null;
};

export const AdmissionsProvider = ({ children }) => {
  const { socket } = useSocket();
  const [sessionEpoch, setSessionEpoch] = useState(0);

  useEffect(() => {
    return API.onTokenChange(() => {
      clearAllAdmissionViews();
      clearAdmissions();
      document.title = DEFAULT_DOCUMENT_TITLE;
      setSessionEpoch((currentEpoch) => currentEpoch + 1);
    });
  }, []);

  useEffect(() => {
    if (!socket) return;
    const onUpsert = (doc) => {
      broadcastAdmissionUpsert(doc);
      if (document.visibilityState === "hidden") return;
      if (!admissionMatchesAnyCachedView(doc)) return;
      setTimeout(() => prefetchAdmission(doc._id), Math.random() * EVENT_PREFETCH_JITTER_MS);
    };
    const onDelete = ({ _id }) => broadcastAdmissionDelete(_id);
    const onReconnect = () => catchUpAllViews();
    socket.on("admission:upsert", onUpsert);
    socket.on("admission:delete", onDelete);
    socket.on("connect", onReconnect);
    return () => {
      socket.off("admission:upsert", onUpsert);
      socket.off("admission:delete", onDelete);
      socket.off("connect", onReconnect);
    };
  }, [socket]);

  const value = useMemo(() => ({ ready: Boolean(socket), sessionEpoch }), [socket, sessionEpoch]);
  return (
    <AdmissionsContext.Provider value={value}>
      <AdmissionTabAttention />
      {children}
    </AdmissionsContext.Provider>
  );
};
